import type { UploadedFile } from '../utils/uploadHelpers'
import { totalSeconds } from '../utils/referenceLimits'
import SideTooltip from '../../../components/SideTooltip'
import { Icon } from '../../../../public/icons/Icon.tsx'

export interface MultiAudioRowProps {
	audios: UploadedFile[]
	maxAudios: number
	/** Суммарный потолок аудио у модели, с (Seedance 2.5 — 30, 2.0 — 15). */
	maxTotalSeconds?: number
	/** Форматы для окна выбора файла по модели; без него — общий список компонента. */
	accept?: string
	isLoading: boolean
	isUploading: boolean
	inputRef: React.RefObject<HTMLInputElement | null>
	onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: (idx: number) => void
	tooltip?: string
	/** Пикер источника («Загрузить / Из моих генераций») у кнопки добавления. */
	onShowPicker?: (buttonEl: HTMLElement) => void
	/** Скелет-плитки пустых слотов до лимита (tool-режим: юзер видит,
	 *  сколько файлов можно добавить). В классическом баре не рендерятся. */
	slotSkeletons?: boolean
}

export default function MultiAudioRow({
	audios,
	maxAudios,
	maxTotalSeconds,
	accept,
	isLoading,
	inputRef,
	onUpload,
	onRemove,
	tooltip,
	onShowPicker,
	slotSkeletons,
}: MultiAudioRowProps) {
	const totalDuration = totalSeconds(audios as (UploadedFile & { duration?: number })[])
	// Без лимита у модели предупреждения нет: число, которого мы не знаем, не показываем.
	const overLimit = maxTotalSeconds != null && totalDuration > maxTotalSeconds

	const addBtn = (
		<button
			type="button"
			className="gm-upload-btn gm-upload-btn--sm"
			onClick={(e) => {
				if (onShowPicker) onShowPicker(e.currentTarget)
				else inputRef.current?.click()
			}}
			disabled={isLoading}
			aria-label="Добавить аудио"
		>
			<Icon id="gen-audio" size={20} />
		</button>
	)
	return (
		<>
			<div className="gm-multi-row">
				{/* Обёртка тумб: в баре display:contents, в tool-режиме — зона со скроллом. */}
				<div className="gm-multi-row__items">
				{audios.map((a, idx) => {
					const filename = a.file.name || `audio ${idx + 1}`
					return (
						<div key={a.preview} className="gm-upload-thumb gm-upload-thumb--sm gm-upload-thumb--audio">
							<div className="gm-upload-thumb__media gm-upload-thumb__media--audio" title={filename}>
								<Icon id="gen-audio" size={20} />
								<span className="gm-upload-thumb__label">{filename.slice(0, 12)}</span>
							</div>
							{!a.url && <div className="gm-upload-thumb__overlay">…</div>}
							{a.url && (
								<div className="gm-upload-thumb__ok">
									<Icon id="gen-check" size={10} />
								</div>
							)}
							<button
								className="gm-upload-thumb__remove"
								onClick={() => onRemove(idx)}
								aria-label="Удалить"
							>
								<Icon id="gen-close" size={14} />
							</button>
						</div>
					)
				})}
				{slotSkeletons &&
					Array.from({ length: Math.max(0, maxAudios - audios.length) }).map((_, i) => (
						<span key={`skel-${i}`} className="gm-upload-skel" aria-hidden="true" />
					))}
				</div>
				{audios.length < maxAudios &&
					(tooltip ? <SideTooltip text={tooltip}>{addBtn}</SideTooltip> : addBtn)}
				<input
					ref={inputRef}
					type="file"
					accept={accept || "audio/wav,audio/mpeg,audio/mp3,audio/aac,audio/mp4,audio/ogg,.wav,.mp3,.aac,.m4a,.ogg"}
					multiple
					onChange={onUpload}
					// isUploading НЕ блокирует: параллельные загрузки легитимны (см. MultiImgRow)
					disabled={isLoading}
					style={{ display: 'none' }}
				/>
			</div>
			{overLimit && (
				<div
					className="gm-multi-row__warning"
					style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: 4 }}
				>
					⚠ Суммарная длительность аудио: {Math.round(totalDuration)} сек (максимум {maxTotalSeconds} сек)
				</div>
			)}
		</>
	)
}
